/**
 * web/src/components/gates/ApproveConfirm.tsx
 * Confirmation dialog before sending an email-gate approval (handoff §Pages
 * §Gate Review — "Approve & send → optimistic toast"). Plain Approve gates
 * (data-gate) skip this and submit directly.
 *
 * When the body contains unfilled single-brace placeholders (e.g.
 * `{Registration Deadline}`, `{Location}`), the modal shows a red warning
 * panel listing them and requires the reviewer to tick "I know — send
 * anyway" before the Send button enables. Prevents accidental sends of
 * emails with literal `{...}` placeholders to sponsors.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../shared/Button';
import { Icon } from '../../lib/icons';

/** Public props for ApproveConfirm. */
export interface ApproveConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the reviewer clicks Send email. */
  onConfirm: () => void;
  recipient: string;
  subject: string;
  /**
   * Unique placeholder keys (no braces) found in the current body. Empty
   * when none — the modal shows a normal confirmation in that case.
   * Non-empty → modal shows a warning + requires explicit ack before Send.
   */
  unfilledPlaceholders?: string[];
  /** Number of files attached to the email (0 / undefined hides the line). */
  attachmentCount?: number;
  /** When true, disables the Send button (e.g., while the mutation is pending). */
  isSubmitting?: boolean;
}

/**
 * Email-send confirmation modal. Shows recipient + subject so the reviewer
 * can sanity-check before the workflow fires the send. When unfilled
 * placeholders are present, escalates to a red warning + requires explicit
 * acknowledgement so the reviewer can't fat-finger a send with `{...}` in
 * the body.
 *
 * @sideEffects none — caller owns the open state and the confirm handler
 * @idempotent yes
 */
export function ApproveConfirm({
  open,
  onOpenChange,
  onConfirm,
  recipient,
  subject,
  unfilledPlaceholders,
  attachmentCount,
  isSubmitting,
}: ApproveConfirmProps) {
  const hasPlaceholders = !!unfilledPlaceholders && unfilledPlaceholders.length > 0;
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset the ack checkbox each time the modal opens so an old tick
  // doesn't carry over from a prior partially-edited body.
  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);

  const sendDisabled = isSubmitting || (hasPlaceholders && !acknowledged);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {hasPlaceholders ? 'Send with unfilled placeholders?' : 'Send email and approve?'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {hasPlaceholders && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2.5 rounded-md border border-failed-border bg-failed-soft px-3 py-2.5 text-failed"
            >
              <Icon name="alert" size={14} className="mt-0.5 shrink-0" />
              <div className="text-[12px] leading-[1.55] min-w-0">
                <div className="font-semibold mb-1">
                  {unfilledPlaceholders!.length} unfilled placeholder
                  {unfilledPlaceholders!.length === 1 ? '' : 's'} — the sponsor will see these
                  verbatim
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {unfilledPlaceholders!.map((k) => (
                    <span
                      key={k}
                      className="inline-flex items-center gap-1 rounded-sm border border-failed-border bg-bg px-1.5 py-0.5 font-mono text-[11px] text-failed"
                    >
                      {'{'}
                      {k}
                      {'}'}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
          <p className="mb-3 text-text">
            The workflow will send an email to <strong className="text-text">{recipient}</strong>{' '}
            and resume.
          </p>
          <p className="text-text-muted text-[12px]">
            Subject: <span className="text-text">{subject}</span>
          </p>
          {!!attachmentCount && attachmentCount > 0 && (
            <p className="mt-1.5 flex items-center gap-1.5 text-text-muted text-[12px]">
              <Icon name="paperclip" size={12} />
              <span>
                {attachmentCount} attachment{attachmentCount === 1 ? '' : 's'}
              </span>
            </p>
          )}
          {hasPlaceholders && (
            <label className="mt-4 flex items-start gap-2 text-[13px] text-text cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-0.5 h-4 w-4 cursor-pointer accent-failed"
              />
              <span>
                I know the body still has{' '}
                <strong className="text-failed">{unfilledPlaceholders!.length}</strong> placeholder
                {unfilledPlaceholders!.length === 1 ? '' : 's'} — send anyway.
              </span>
            </label>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="default" onClick={() => onOpenChange(false)}>
            {hasPlaceholders ? 'Back — fix placeholders' : 'Cancel'}
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={sendDisabled}>
            <Icon name="send" size={12} />
            Send email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
